/**
 * 캠페인 관련 유틸리티 함수
 */

/**
 * 지역이 성남 또는 수원인지 확인
 * @param loadAddr localStorage의 loadAddr 값 (예: "경기도 성남시 수정구 복정동")
 * @returns 성남 또는 수원이 포함되어 있으면 true
 */
export const isEligibleRegion = (loadAddr: string | null): boolean => {
  if (!loadAddr) return false;
  return loadAddr.includes("성남") || loadAddr.includes("수원");
};

/**
 * loadAddr에서 지역 추출 (성남 또는 수원)
 * @param loadAddr localStorage의 loadAddr 값 (예: "경기도 성남시 수정구 복정동")
 * @returns "성남" | "수원" | null
 */
export const getRegionFromLoadAddr = (
  loadAddr: string | null
): "성남" | "수원" | null => {
  if (!loadAddr) return null;
  if (loadAddr.includes("성남")) return "성남";
  if (loadAddr.includes("수원")) return "수원";
  return null;
};

/**
 * 캠페인이 특정 지역에 속하는지 확인
 * @param campaign 캠페인 객체
 * @param region 지역 ("성남" | "수원")
 * @returns 캠페인의 brand.roadAddr에 해당 지역이 포함되어 있으면 true
 */
export const isCampaignInRegion = (
  campaign: any,
  region: "성남" | "수원"
): boolean => {
  if (!campaign?.brand?.roadAddr) return false;
  return campaign.brand.roadAddr.includes(region);
};

/**
 * 캠페인이 참여 가능한 상태인지 확인 (현재 시간이 캠페인 기간에 포함되는지)
 * @param startAt 캠페인 시작 시간
 * @param endAt 캠페인 종료 시간
 * @returns 현재 시간이 캠페인 기간에 포함되면 true (참여 가능)
 */
export const isCampaignAvailable = (
  startAt: string | Date,
  endAt: string | Date
): boolean => {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const now = new Date();
  // 현재 시간이 캠페인 기간에 포함되면 참여 가능
  return start <= now && end >= now;
};

/**
 * 캠페인 상태 반환
 * @param startAt 캠페인 시작 시간
 * @param endAt 캠페인 종료 시간
 * @returns "available" | "ended"
 */
export const getCampaignStatus = (
  startAt: string | Date,
  endAt: string | Date
): "available" | "ended" => {
  const end = new Date(endAt);
  const now = new Date();

  if (end < now) {
    return "ended";
  }

  // 종료되지 않았으면 available
  return "available";
};

/**
 * 캠페인 시간 포맷팅
 * @param dateTime 날짜/시간
 * @returns 포맷된 시간 문자열 (예: "1월 15일 14시")
 */
export const formatCampaignTime = (dateTime: string | Date): string => {
  const date = new Date(dateTime);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = date.getHours();
  return `${month}월 ${day}일 ${hour}시`;
};

export const getFormattedRunTime = (run: any) => {
  if (!run) return "";
  const startDate = new Date(run.startAt);
  const endDate = new Date(run.endAt);

  const getMonthDay = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
  const getAmPmHour = (d: Date) => {
    const h = d.getHours();
    const ampm = h < 12 ? "오전" : "오후";
    const hour = h % 12 || 12; // 0시는 12시로 표시
    return `${ampm} ${hour}시`;
  };

  // 시작일과 종료일이 모두 00시인 경우
  const isStartMidnight =
    startDate.getHours() === 0 && startDate.getMinutes() === 0;
  const isEndMidnight = endDate.getHours() === 0 && endDate.getMinutes() === 0;
  const timeDiff = endDate.getTime() - startDate.getTime();
  const oneDayInMs = 86400000; // 24시간

  if (isStartMidnight && isEndMidnight) {
    // 정확히 24시간 (1일)인 경우: "1/16일 하루동안"
    if (timeDiff === oneDayInMs) {
      return `${getMonthDay(startDate)}일 하루동안`;
    }
    // 1일 이상 차이인 경우: 종료일을 하루 빼서 표기 (예: 1/16 00시 ~ 1/18 00시 → 1/16~1/17)
    const displayEndDate = new Date(endDate);
    displayEndDate.setDate(displayEndDate.getDate() - 1);
    return `${getMonthDay(startDate)}~${getMonthDay(displayEndDate)}`;
  }

  const isSameDay =
    startDate.getDate() === endDate.getDate() &&
    startDate.getMonth() === endDate.getMonth();

  const startStr = `${getMonthDay(startDate)} ${getAmPmHour(startDate)}`;

  if (isSameDay) {
    return `${startStr} ~ ${getAmPmHour(endDate)}`;
  } else {
    return `${startStr} ~ ${getMonthDay(endDate)} ${getAmPmHour(endDate)}`;
  }
};

/**
 * 오늘 또는 내일 시작하는 캠페인인지 확인
 * @param startAt 캠페인 시작 시간
 * @returns 오늘 또는 내일 시작하면 true
 */
export const isTodayOrTomorrow = (startAt: string | Date): boolean => {
  const start = new Date(startAt);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(23, 59, 59, 999);

  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  return start >= todayStart && start <= tomorrow;
};

/**
 * Submission 상태 텍스트 변환
 * @param status SubmissionStatus
 * @returns 상태 텍스트
 */
export const getSubmissionStatusText = (status: string): string => {
  switch (status) {
    case "PENDING":
      return "미제출";
    case "SUBMITTED":
      return "검토 중";
    case "APPROVED":
      return "인증 완료";
    case "REJECTED":
      return "인증 실패";
    case "EXPIRED":
      return "만료";
    default:
      return status;
  }
};

/**
 * Submission 상태 색상 클래스 반환
 * @param status SubmissionStatus
 * @returns Tailwind CSS 클래스
 */
export const getSubmissionStatusColor = (status: string): string => {
  switch (status) {
    case "PENDING":
      return "bg-gray-100 text-gray-700";
    case "SUBMITTED":
      return "bg-yellow-100 text-yellow-700";
    case "APPROVED":
      return "bg-green-100 text-green-700";
    case "REJECTED":
      return "bg-red-100 text-red-700";
    case "EXPIRED":
      return "bg-gray-100 text-gray-500";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

/**
 * Submission 제출 가능 기간 확인
 * @param windowStartAt 제출 시작 시간
 * @param windowEndAt 제출 종료 시간
 * @returns 현재 시간이 제출 가능 기간 내에 있으면 true
 */
export const isSubmissionWindowActive = (
  windowStartAt: string | Date,
  windowEndAt: string | Date
): boolean => {
  const start = new Date(windowStartAt);
  const end = new Date(windowEndAt);
  const now = new Date();
  return start <= now && end >= now;
};

/**
 * Submission 제출 가능 여부 판단
 * @param submission 현재 submission
 * @param previousSubmissions 이전 submission 배열 (stepOrder 순서대로 정렬되어 있어야 함)
 * @returns 제출 가능하면 true
 */
export const canSubmitSubmission = (
  submission: any,
  previousSubmissions: any[]
): boolean => {
  // 이미 제출했거나 승인된 경우 제출 불가
  if (submission.status === "APPROVED" || submission.status === "SUBMITTED") {
    return false;
  }

  // window가 만료된 경우 제출 불가
  if (
    !isSubmissionWindowActive(submission.windowStartAt, submission.windowEndAt)
  ) {
    return false;
  }

  // 이전 필수 submission이 모두 "제출됨 이상"인지 확인
  const currentStepOrder = submission.stepOrder;
  for (const prevSubmission of previousSubmissions) {
    if (prevSubmission.stepOrder >= currentStepOrder) {
      break;
    }
    // 이전 submission이 필수이고 제출되지 않은 경우
    if (
      prevSubmission.step?.isRequired &&
      prevSubmission.status !== "SUBMITTED" &&
      prevSubmission.status !== "APPROVED"
    ) {
      return false;
    }
  }

  return true;
};

/**
 * 캠페인의 먹는 방식에 따른 안내 메시지 반환
 * @param eatingMethods 먹는 방식 배열 (예: ["포장", "매장식사"])
 * @returns 안내 메시지
 */
export const getEatingMethodMessage = (
  eatingMethods: string[] | null | undefined
): string | null => {
  if (!eatingMethods || eatingMethods.length === 0) {
    return null;
  }

  const hasPacking = eatingMethods.includes("포장");
  const hasDineIn = eatingMethods.includes("매장식사");

  if (hasPacking && hasDineIn) {
    return "포장과 매장식사 모두 가능한 미션입니다.";
  } else if (hasPacking) {
    return "포장만 가능한 미션입니다!";
  } else if (hasDineIn) {
    return "매장식사만 가능한 미션입니다.";
  }

  return null;
};

/**
 * 참여 제한 정보를 읽기 쉬운 문구로 변환
 * @param contract Contract 객체 (monthlyParticipationLimit 포함)
 * @returns 참여 제한 문구 (예: "매월 1회 제한") 또는 null
 */
export const getParticipationLimitText = (contract: {
  monthlyParticipationLimit?: number | null;
}): string | null => {
  if (
    contract.monthlyParticipationLimit !== null &&
    contract.monthlyParticipationLimit !== undefined
  ) {
    return `매월 ${contract.monthlyParticipationLimit}회 제한`;
  }
  return null;
};

/**
 * 참여 제한 정보를 상세 설명 문구로 변환
 * @param contract Contract 객체
 * @returns 상세 설명 문구 (예: "매월 1회") 또는 null
 */
export const getParticipationLimitDescription = (contract: {
  monthlyParticipationLimit?: number | null;
}): string | null => {
  if (
    contract.monthlyParticipationLimit === null ||
    contract.monthlyParticipationLimit === undefined
  ) {
    return null;
  }

  return `매월 ${contract.monthlyParticipationLimit}회`;
};

/**
 * 남은 참여 횟수 텍스트 반환
 * @param contract Contract 객체
 * @returns 남은 참여 횟수 텍스트 (예: "남은 횟수: 2회") 또는 null
 */
export const getRemainingParticipationCountText = (contract: {
  remainingParticipationCount?: number | null;
}): string | null => {
  if (
    contract.remainingParticipationCount !== null &&
    contract.remainingParticipationCount !== undefined
  ) {
    return `남은 횟수: ${contract.remainingParticipationCount}회`;
  }
  return null;
};

/**
 * 참여 가능 횟수 텍스트 반환
 * @param contract Contract 객체
 * @returns 참여 가능 횟수 텍스트 (예: "남은 참여 횟수: 2회 (매월 1회)") 또는 null
 * @note 0회일 때도 표시됩니다 (이미 제한을 초과한 경우)
 */
export const getAvailableParticipationCountText = (contract: {
  remainingParticipationCount?: number | null;
  monthlyParticipationLimit?: number | null;
}): string | null => {
  // null이 아니고 undefined가 아닌 경우 (0 포함) 표시
  if (
    contract.remainingParticipationCount !== null &&
    contract.remainingParticipationCount !== undefined
  ) {
    const remainingText = `남은 참여 횟수: ${contract.remainingParticipationCount}회`;

    // monthlyParticipationLimit이 있으면 괄호 안에 표시
    if (
      contract.monthlyParticipationLimit !== null &&
      contract.monthlyParticipationLimit !== undefined
    ) {
      return `${remainingText} (매월 ${contract.monthlyParticipationLimit}회)`;
    }

    return remainingText;
  }
  return null;
};
